import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Upload, ImagePlus } from "lucide-react";
import { API, authHeader, fileToCompressedDataURL } from "../../lib/adminApi";

export default function BeforeAfterTab({ onAuthError }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", treatment: "", description: "" });
  const [beforeImg, setBeforeImg] = useState("");
  const [afterImg, setAfterImg] = useState("");
  const [compositeImg, setCompositeImg] = useState("");
  const [mode, setMode] = useState("combined");
  const [saving, setSaving] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const compositeRef = useRef(null);

  const load = () => axios.get(`${API}/before-after`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleFile = async (e, which) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await fileToCompressedDataURL(file);
      if (which === "before") setBeforeImg(dataUrl);
      else if (which === "after") setAfterImg(dataUrl);
      else setCompositeImg(dataUrl);
    } catch {
      toast.error("Could not read image");
    }
  };

  const reset = () => {
    setForm({ title: "", treatment: "", description: "" });
    setBeforeImg(""); setAfterImg(""); setCompositeImg("");
    if (beforeRef.current) beforeRef.current.value = "";
    if (afterRef.current) afterRef.current.value = "";
    if (compositeRef.current) compositeRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Please add a title.");
    if (mode === "combined" && !compositeImg) return toast.error("Upload the combined image.");
    if (mode === "pair" && (!beforeImg || !afterImg)) return toast.error("Upload both images.");
    setSaving(true);
    try {
      const payload = mode === "combined"
        ? { ...form, composite_image: compositeImg }
        : { ...form, before_image: beforeImg, after_image: afterImg };
      await axios.post(`${API}/before-after`, payload, authHeader());
      toast.success("Added to gallery");
      reset();
      load();
    } catch (err) {
      if (err?.response?.status === 401) onAuthError && onAuthError();
      else toast.error("Could not save. Try smaller images.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/before-after/${id}`, authHeader());
      toast.success("Removed");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const UploadBox = ({ label, img, inputRef, which }) => (
    <div>
      <span className="block text-[11px] tracking-[0.16em] uppercase text-[#8A857A] mb-2">{label}</span>
      <label className="block cursor-pointer border border-dashed border-[#cbbfa8] hover:border-[#C6A15B] transition-colors aspect-[4/3] overflow-hidden bg-[#F7F2EA] relative">
        {img ? (
          <img src={img} alt={label} className="w-full h-full object-cover" />
        ) : (
          <span className="absolute inset-0 flex flex-col items-center justify-center text-[#8A857A]">
            <ImagePlus size={26} className="mb-2 text-[#C6A15B]" />
            <span className="text-xs">Click to upload</span>
          </span>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e, which)} />
      </label>
    </div>
  );

  return (
    <div>
      <h1 className="font-serif-display text-3xl text-[#1A1A1A] mb-2">Before &amp; After</h1>
      <p className="text-[#8A857A] text-sm mb-8">Images are auto-compressed for fast loading.</p>

      <form onSubmit={submit} className="bg-white border border-[#E4DBCB] p-6 mb-12">
        <div className="inline-flex border border-[#E4DBCB] mb-6">
          <button type="button" onClick={() => setMode("combined")} className={`px-5 py-2.5 text-[11px] tracking-[0.16em] uppercase ${mode === "combined" ? "bg-[#0C0C0C] text-[#C6A15B]" : "text-[#8A857A]"}`}>Combined Image</button>
          <button type="button" onClick={() => setMode("pair")} className={`px-5 py-2.5 text-[11px] tracking-[0.16em] uppercase ${mode === "pair" ? "bg-[#0C0C0C] text-[#C6A15B]" : "text-[#8A857A]"}`}>Separate Before + After</button>
        </div>
        {mode === "combined" ? (
          <div className="mb-6 max-w-md">
            <UploadBox label="Combined Before / After Image" img={compositeImg} inputRef={compositeRef} which="composite" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <UploadBox label="Before" img={beforeImg} inputRef={beforeRef} which="before" />
            <UploadBox label="After" img={afterImg} inputRef={afterRef} which="after" />
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title *" className="bg-transparent border-b border-[#D8CCB8] focus:border-[#C6A15B] outline-none py-3 text-[#1A1A1A]" />
          <input value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} placeholder="Treatment tag" className="bg-transparent border-b border-[#D8CCB8] focus:border-[#C6A15B] outline-none py-3 text-[#1A1A1A]" />
        </div>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={2} className="w-full bg-transparent border-b border-[#D8CCB8] focus:border-[#C6A15B] outline-none py-3 text-[#1A1A1A] resize-none mb-6" />
        <button type="submit" disabled={saving} className="flex items-center gap-2 bg-[#0C0C0C] text-[#C6A15B] text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-[#1a1a1a] transition-colors disabled:opacity-60">
          <Upload size={16} /> {saving ? "Saving..." : "Add to Gallery"}
        </button>
      </form>

      <h2 className="font-serif-display text-2xl text-[#1A1A1A] mb-6">Gallery ({items.length})</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((it) => (
          <div key={it.id} className="bg-white border border-[#E4DBCB] overflow-hidden">
            {it.composite_image ? (
              <img src={it.composite_image} alt={it.title} className="w-full aspect-square object-cover" />
            ) : (
              <div className="grid grid-cols-2">
                <img src={it.before_image} alt="Before" className="aspect-square object-cover w-full" />
                <img src={it.after_image} alt="After" className="aspect-square object-cover w-full" />
              </div>
            )}
            <div className="p-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-serif-display text-lg text-[#1A1A1A] leading-tight">{it.title}</h3>
                {it.treatment ? <span className="text-[10px] tracking-[0.18em] uppercase text-[#C6A15B]">{it.treatment}</span> : null}
              </div>
              <button onClick={() => remove(it.id)} className="text-[#b04a3f] hover:text-[#7f322a] shrink-0" aria-label="Delete">
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
